import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import { Dropdown } from 'antd';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { isChildNode } from 'utils/is-child-node';
import { DateField } from '../../fields';
import {
  DateFieldFilter,
  DateFieldFilterOperationType,
  DateFieldFilterOperationTypes,
  isDefault,
  isOptional,
} from '../../filters/date';
import { Trigger } from './components';
import { format } from './format';
import { isBasic } from './helpers';
import { renderOverlay } from './overlay';

interface Props {
  filter: DateFieldFilter;
  onChange: (filter: DateFieldFilter) => void;
  getFilterOperationTypeOptions?: (field: DateField) => DateFieldFilter[];
  getOperationOptionRenderer?: (
    type: DateFieldFilterOperationType,
    filter?: DateFieldFilter,
  ) => null | JSX.Element;
}

const defaultOptions = (field: DateField): DateFieldFilter[] =>
  DateFieldFilterOperationTypes.filter((x) => isDefault(x))
    .filter((x) => !field.metaData?.required || !isOptional(x))
    .map(
      (type) =>
        ({
          field,
          type,
        } as any as DateFieldFilter),
    );

export const DateCriteria: FC<Props> = ({
  filter,
  onChange,
  getFilterOperationTypeOptions = defaultOptions,
  getOperationOptionRenderer,
}) => {
  const [visible, setVisible] = useState(false);
  const [advanced, setAdvanced] = useState(!isBasic(filter.type));
  const items = getFilterOperationTypeOptions(filter.field);
  const isChild = isChildNode([
    'date-criteria',
    'ant-dropdown',
    'ant-picker-dropdown',
  ]);
  const close = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      if (target != null && !isChild(target)) {
        setVisible(false);
      }
    },
    [setVisible, isChild],
  );

  useEffect(() => {
    if (!visible) {
      return () => {};
    }
    document.addEventListener('click', close);

    return () => {
      document.removeEventListener('click', close);
    };
  }, [close, visible]);

  useEffect(() => {
    if (!visible) {
      setAdvanced(!isBasic(filter.type));
    }
  }, [filter.type, setAdvanced, visible]);

  useEffect(() => {
    const hasBasicFilterOptions =
      items.filter((x) => isBasic(x.type)).length > 0;

    if (!hasBasicFilterOptions) {
      setAdvanced(true);
    }
  }, [visible, items]);

  const onAdvancedClick = useCallback(() => setAdvanced(true), [setAdvanced]);

  return (
    <Dropdown
      trigger={['click']}
      visible={visible}
      overlay={renderOverlay(
        filter,
        onChange,
        setVisible,
        items,
        onAdvancedClick,
        advanced,
        getOperationOptionRenderer,
      )}
    >
      <Trigger
        data-cy="criteria.op_trigger"
        className="date-criteria"
        onClick={() => setVisible(!visible)}
      >
        <span>{format(filter, getOperationOptionRenderer)}</span>
        <ChevronDown16 />
      </Trigger>
    </Dropdown>
  );
};
